import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment.development';
import { STORAGE_ENUM } from '../../../auth/enums/storage.enum';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { SessionService } from '../../../auth/services/session/session.service';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';
import { AreaCategory } from '../../models/tickets/area-category.model';
import { Employee } from '../../models/tickets/employee.model';
import { Group } from '../../models/tickets/group.model';
import { Media } from '../../models/tickets/media.model';
import { PlagueCategory } from '../../models/tickets/plague-category.model';
import { TicketType } from '../../models/tickets/ticket-type.model';
import { Status, Ticket } from '../../models/tickets/ticket.model';

export interface CatalogResponse<T> {
  total: number;
  page: number;
  page_size: number;
  items: T[];
}

export interface NestingArea {
  id: string;
  name: string;
  job_center: string;
}

export interface Plague {
  id: string;
  name: string;
  job_center: string;
}

@Injectable({
  providedIn: 'root'
})
export class TicketsService {
  private _http = inject(HttpClient);
  private _authService = inject(AuthService);
  private _sessionService = inject(SessionService);
  private _localStorage = inject(LocalStorageService);

  private _baseUrl = `${environment.apiUrl}/administrative`;

  getTicketTypes(query?: string): Observable<TicketType[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/ticket-types/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<TicketType[]>(url, {
      headers
    });
  }

  getMedia(query?: string): Observable<CatalogResponse<Media>> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const jobCenterId = this._sessionService.Customer.job_center.id;
    let url = `${this._baseUrl}/catalogs/media/?job_center=${jobCenterId}&paginate=true&page=1&page_size=15`;

    if (query) {
      url += `&search=${encodeURIComponent(query)}`;
    }

    return this._http.get<CatalogResponse<Media>>(url, {
      headers
    });
  }

  getNestingAreas(query?: string): Observable<CatalogResponse<NestingArea>> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const jobCenterId = this._sessionService.Customer.job_center.id;
    let url = `${this._baseUrl}/pest-control/nesting-areas/?job_center=${jobCenterId}&shareJobCenter=${jobCenterId}&page=1&page_size=15`;

    if (query) {
      url += `&search=${encodeURIComponent(query)}`;
    }

    return this._http.get<CatalogResponse<NestingArea>>(url, {
      headers
    });
  }

  getEmployees(): Observable<Employee[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting employees'));
    }

    const jobCenterId = this._sessionService.Customer.job_center.id;

    return this._http.get<Employee[]>(`${this._baseUrl}/employees/?job_center=${jobCenterId}`, {
      headers
    });
  }

  getGroups(): Observable<Group[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    return this._http.get<Group[]>(`${this._baseUrl}/groups/`, {
      headers
    });
  }

  getAreaCategories(query?: string): Observable<AreaCategory[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/area-categories/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<AreaCategory[]>(url, {
      headers
    });
  }

  getPlagueCategories(query?: string): Observable<PlagueCategory[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    let url = `${this._baseUrl}/catalogs/plague-categories/?page=1&page_size=10`;

    if (query) {
      url += `&search=${query}`;
    }

    return this._http.get<PlagueCategory[]>(url, {
      headers
    });
  }

  getPlagues(query?: string): Observable<CatalogResponse<Plague>> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const jobCenterId = this._sessionService.Customer.job_center.id;
    let url = `${this._baseUrl}/catalogs/plagues/?job_center=${jobCenterId}&paginate=true&page=1&page_size=15`;

    if (query) {
      url += `&search=${encodeURIComponent(query)}`;
    }

    return this._http.get<CatalogResponse<Plague>>(url, {
      headers
    });
  }

  getTicketsStatuses(query?: any): Observable<Status[]> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting statuses'));
    }

    let statusesUrl = `${this._baseUrl}/catalogs/statuses/?module=TK`;

    if (query) {
      statusesUrl += `&search=${query}`;
    }

    return this._http.get<Status[]>(statusesUrl, {
      headers
    });
  }

  getTickets(filters: any): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error getting tickets'));
    }

    const isMainCustomer = this._sessionService.Customer.is_main;
    const customerId = this._sessionService.Customer.id;
    let ticketsUrl = `${this._baseUrl}/tickets/?customer=${customerId}`;

    if (isMainCustomer) {
      ticketsUrl += `&customer__main_customer=${customerId}`;
    }

    if (filters.search) {
      ticketsUrl += `&search=${filters.search}`;
    }

    if (filters.page) {
      ticketsUrl += `&page=${filters.page}`;
    }

    if (filters.page_size) {
      ticketsUrl += `&page_size=${filters.page_size}`;
    }

    if (filters.status) {
      ticketsUrl += `&status=${filters.status}`;
    }

    if (filters.ticket_type) {
      ticketsUrl += `&ticket_type=${filters.ticket_type}`;
    }

    return this._http.get<any>(ticketsUrl, {
      headers
    });
  }

  createTicket(
    comments: string,
    ticketType: string,
    media: string,
    nestingArea: string[],
    plagueCategory: string[],
    plague: string[],
    closeDate: string
  ): Observable<Ticket> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al guardar el ticket'));
    }

    const customerId = this._sessionService.Customer.id;
    const jobCenterId = this._sessionService.Customer.job_center.id;

    const body = {
      ticket_type: ticketType,
      customer: customerId,
      close_date: closeDate,
      media,
      nesting_area: nestingArea,
      plague_category: plagueCategory,
      plague,
      comments,
      job_center: jobCenterId
    };

    return this._http.post<Ticket>(`${this._baseUrl}/tickets/`, body, {
      headers
    });
  }

  patchTicket(data: any, ticketId: string): Observable<any> {
    const token = this._localStorage.get(STORAGE_ENUM.TOKEN);

    const headers = {
      Authorization: `Bearer ${token}`
    };

    if (!token) {
      return throwError(() => new Error('Error al actualizar el ticket'));
    }

    return this._http.patch<any>(`${this._baseUrl}/tickets/${ticketId}/`, data, {
      headers
    });
  }
}
